import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { UserProfile } from '@/types/assessment';

interface UseBookingPaymentProps {
  userId?: string;
  userProfile?: UserProfile | null;
  bookingId: string;
  finalAmount: number;
}

// For development mode testing
const DEV_MODE = import.meta.env.DEV || false;

export const useBookingPayment = ({
  userId,
  userProfile,
  bookingId,
  finalAmount,
}: UseBookingPaymentProps) => {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [razorpayReady, setRazorpayReady] = useState(false);

  // Check if Razorpay script is loaded
  useEffect(() => {
    if (window.Razorpay) {
      setRazorpayReady(true);
      return;
    }

    // Listen for Razorpay loaded event from RazorpayScript component
    const handleRazorpayLoaded = () => {
      console.log('useBookingPayment: Razorpay loaded event received');
      setRazorpayReady(true);
    };
    
    window.addEventListener('razorpay_loaded', handleRazorpayLoaded);
    
    // Check every second in case the event was missed
    const checkInterval = setInterval(() => {
      if (window.Razorpay) {
        console.log('useBookingPayment: Razorpay detected through interval check');
        setRazorpayReady(true);
        clearInterval(checkInterval);
      }
    }, 1000);
    
    return () => {
      window.removeEventListener('razorpay_loaded', handleRazorpayLoaded);
      clearInterval(checkInterval);
    };
  }, []);

  // Development mode helpers for testing
  const devModeCreatePayment = async (payload: any) => {
    console.log('DEV MODE: Using mock implementation for create-booking-payment');
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return {
      success: true,
      keyId: "rzp_test_mock_key_id",
      orderId: payload.bookingId,
      razorpayOrderId: `order_dev_${Math.random().toString(36).substring(2, 10)}`,
      amount: payload.amount,
      currency: "INR",
      name: payload.name || "User",
      email: payload.email || "",
      phone: payload.phone || ""
    };
  };

  const devModeVerifyPayment = async (payload: any) => {
    console.log('DEV MODE: Using mock implementation for verify-booking-payment');
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    if (DEV_MODE) {
      try {
        await supabase
          .from('bookings')
          .update({
            payment_status: 'completed',
            razorpay_payment_id: payload.razorpayPaymentId
          })
          .eq('id', payload.bookingId);
      } catch (error) {
        console.error('DEV MODE: Error updating booking status:', error);
      }
    }
    
    return {
      success: true,
      bookingId: payload.bookingId,
      message: "Payment verified successfully (dev mode)"
    };
  };

  const handlePaymentSuccess = async (
    bookingId: string,
    razorpayPaymentId: string,
    razorpayOrderId: string,
    razorpaySignature: string
  ) => {
    try {
      console.log("Verifying booking payment...", { bookingId, razorpayPaymentId });
      
      let verificationData;
      
      if (DEV_MODE) {
        verificationData = await devModeVerifyPayment({
          bookingId,
          razorpayPaymentId,
          razorpayOrderId,
          razorpaySignature
        });
      } else {
        const response = await supabase.functions.invoke('verify-booking-payment', {
          body: {
            bookingId,
            razorpayPaymentId,
            razorpayOrderId,
            razorpaySignature
          }
        });
        
        if (response.error) {
          throw new Error(response.error.message || 'Payment verification failed');
        }
        verificationData = response.data;
      }

      if (!verificationData || !verificationData.success) {
        throw new Error(verificationData?.error || 'Payment verification failed');
      }
      
      toast.success('Payment successful!');
      navigate(`/booking-payment-success?booking=${bookingId}`);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Payment verification error:', error);
      toast.error(errorMessage || 'Payment verification failed');
      
      if (razorpayPaymentId && razorpayOrderId) {
        // Even if verification fails, if we have payment IDs, we can still try to proceed
        navigate(`/booking-payment-success?booking=${bookingId}&verification=pending`);
      } else {
        setIsProcessing(false);
      }
    }
  };

  const processPayment = async () => {
    if (!userId || !userProfile) {
      toast.error('Please log in to continue');
      navigate('/login');
      return;
    }

    if (!bookingId) {
      toast.error('Invalid booking information');
      return;
    }

    if (!finalAmount || finalAmount <= 0) {
      toast.error('Invalid payment amount');
      return;
    }

    if (!razorpayReady && !window.Razorpay) {
      toast.info('Payment system is initializing. Please try again in a few seconds.');
      return;
    }

    setIsProcessing(true);

    try {
      console.log(`Processing payment for booking:`, {
        userId,
        bookingId,
        amount: finalAmount,
        name: userProfile?.name,
        email: userProfile?.email,
        phone: userProfile?.phone
      });
      
      let paymentData;
      
      if (DEV_MODE) {
        paymentData = await devModeCreatePayment({
          userId,
          bookingId,
          amount: finalAmount,
          name: userProfile?.name || "User",
          email: userProfile?.email || "",
          phone: userProfile?.phone || ""
        });
      } else {
        console.log("Calling create-booking-payment with data:", {
          userId,
          bookingId,
          amount: finalAmount,
          name: userProfile?.name,
          email: userProfile?.email,
          phone: userProfile?.phone
        });
        
        const response = await supabase.functions.invoke('create-booking-payment', {
          body: {
            userId,
            bookingId,
            amount: finalAmount,
            name: userProfile?.name || "User",
            email: userProfile?.email || "",
            phone: userProfile?.phone || ""
          }
        });
        
        if (response.error) {
          console.error("Edge function call failed:", response.error);
          throw new Error(response.error.message || 'Failed to create payment');
        }
        
        paymentData = response.data;
        console.log("Payment data received:", paymentData);
      }

      const options = {
        key: paymentData.keyId,
        amount: paymentData.amount * 100,
        currency: paymentData.currency || "INR",
        name: 'Career Guide Kerala',
        description: 'Session Booking',
        order_id: paymentData.razorpayOrderId,
        handler: function (response: any) {
          handlePaymentSuccess(
            bookingId,
            response.razorpay_payment_id,
            response.razorpay_order_id,
            response.razorpay_signature
          );
        },
        prefill: {
          name: userProfile?.name || "",
          email: userProfile?.email || "",
          contact: userProfile?.phone || ""
        },
        theme: {
          color: '#1976D2'
        },
        modal: {
          ondismiss: function() {
            setIsProcessing(false);
            toast.info('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Payment processing error:', error);
      
      if (errorMessage.includes('network') || errorMessage.includes('Failed to fetch')) {
        toast.error('Network error. Please check your internet connection and try again.');
      } else {
        toast.error(errorMessage || 'Payment processing failed. Please try again later.');
      }
      
      setIsProcessing(false);
    }
  };

  return {
    isProcessing,
    setIsProcessing,
    processPayment,
    razorpayReady: razorpayReady || Boolean(window.Razorpay)
  };
};

export default useBookingPayment;
